import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ReferenceLine, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GradeKpis, Kpi } from "@/features/overview/telas/Kpi";
import { CORES } from "@/features/overview/series";
import { formatDateBr, formatMesAno } from "@/lib/format";
import { useChurn, useCoortes, useTrials, type Periodo } from "./api";
import { SITUACAO_ADMIN_LABEL, formatAcesso, formatNumero, formatPercentual } from "./formato";

export function AbaTrialsChurn({ periodo }: { periodo: Periodo }) {
  return (
    <div className="space-y-6">
      <Trials periodo={periodo} />
      <Coortes />
      <Churn />
    </div>
  );
}

function Secao({ titulo, descricao, acao, children }: { titulo: string; descricao?: string; acao?: React.ReactNode; children: React.ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id={id} className="text-base font-semibold text-foreground">
            {titulo}
          </h2>
          {descricao && <p className="text-sm text-muted-foreground">{descricao}</p>}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

function Trials({ periodo }: { periodo: Periodo }) {
  const { data, isLoading } = useTrials(periodo);
  const pct = (n?: number) => (data?.trials ? formatPercentual((n ?? 0) / data.trials) : "—");

  return (
    <Secao titulo="Ativação de trials" descricao="Instituições com início de trial no período e quando fizeram o primeiro login.">
      <GradeKpis variante="padrao" colunas={4}>
        <Kpi variante="padrao" rotulo="Trials no período" valor={formatNumero(data?.trials)} isLoading={isLoading} />
        <Kpi
          variante="padrao"
          rotulo="Acessaram em até 3 dias"
          valor={pct(data?.acessaramEm3Dias)}
          detalhe={data ? `${data.acessaramEm3Dias} de ${data.trials}` : undefined}
          cor={CORES.verde}
          isLoading={isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Acessaram em até 7 dias"
          valor={pct(data?.acessaramEm7Dias)}
          detalhe={data ? `${data.acessaramEm7Dias} de ${data.trials}` : undefined}
          isLoading={isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Sem nenhum acesso"
          valor={formatNumero(data ? data.trials - data.acessaram : undefined)}
          detalhe={
            data?.mediaDiasAtePrimeiroAcesso != null
              ? `média de ${data.mediaDiasAtePrimeiroAcesso.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} dias até o 1º login`
              : undefined
          }
          cor={CORES.vermelho}
          isLoading={isLoading}
        />
      </GradeKpis>
      <div className="rounded-xl border border-border bg-card shadow-card">
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !data?.instituicoes.length ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Nenhum trial iniciado no período.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Instituição</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead>Início do trial</TableHead>
                <TableHead>Primeiro login</TableHead>
                <TableHead className="text-right">Dias até o 1º login</TableHead>
                <TableHead>Último login</TableHead>
                <TableHead className="text-right">Logins</TableHead>
                <TableHead className="text-right">Usuários</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.instituicoes.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">
                    <Link to={`/Acessos/Instituicao/${t.id}`} className="hover:underline">
                      {t.nomeFantasia ?? `#${t.id}`}
                    </Link>
                  </TableCell>
                  <TableCell>{SITUACAO_ADMIN_LABEL[t.situacaoAdmin ?? ""] ?? t.situacaoAdmin}</TableCell>
                  <TableCell>{formatDateBr(t.inicioTrial)}</TableCell>
                  <TableCell>
                    {t.primeiroAcesso ? formatAcesso(t.primeiroAcesso) : <span className="font-medium text-red-600">sem acesso</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{t.diasAtePrimeiroAcesso ?? "—"}</TableCell>
                  <TableCell>{formatAcesso(t.ultimoAcesso)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumero(t.logins)}</TableCell>
                  <TableCell className="text-right tabular-nums">{t.usuariosAcessaram}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </Secao>
  );
}

function Coortes() {
  const [meses, setMeses] = useState(6);
  const { data, isLoading } = useCoortes(meses);
  const colunas = data?.reduce((m, c) => Math.max(m, c.retencao.length), 0) ?? 0;

  return (
    <Secao
      titulo="Retenção de novos usuários"
      descricao="Usuários agrupados pelo mês do primeiro login, e quantos % deles voltaram a logar em cada mês seguinte. Usuários cadastrados antes do início do histórico de logins ficam de fora."
      acao={
        <Select value={String(meses)} onValueChange={(v) => setMeses(Number(v))}>
          <SelectTrigger className="w-40" aria-label="Meses de coorte">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6">Últimos 6 meses</SelectItem>
            <SelectItem value="12">Últimos 12 meses</SelectItem>
          </SelectContent>
        </Select>
      }
    >
      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-card">
        {isLoading ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>1º login em</TableHead>
                <TableHead className="text-right">Usuários</TableHead>
                {Array.from({ length: colunas }, (_, k) => (
                  <TableHead key={k} className="text-center">
                    {k === 0 ? "Mês 0" : `+${k}`}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.map((c) => (
                <TableRow key={c.mes}>
                  <TableCell className="whitespace-nowrap">{formatMesAno(c.mes)}</TableCell>
                  <TableCell className="text-right tabular-nums">{c.usuarios}</TableCell>
                  {Array.from({ length: colunas }, (_, k) => {
                    const v = c.retencao[k];
                    return (
                      <TableCell
                        key={k}
                        className="text-center tabular-nums"
                        style={v != null ? { background: `rgba(27, 175, 122, ${0.08 + 0.6 * v})` } : undefined}
                      >
                        {k >= c.retencao.length ? "" : v == null ? "—" : formatPercentual(v)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </Secao>
  );
}

const CONFIG_CHURN: ChartConfig = { mediaLogins: { label: "Canceladas (média)", color: CORES.vermelho } };

function Churn() {
  const [meses, setMeses] = useState(12);
  const { data, isLoading } = useChurn(meses);
  const pontos = (data?.semanas ?? []).map((s) => ({ ...s, rotulo: `${s.semana} sem` }));

  return (
    <Secao
      titulo="Uso antes do cancelamento"
      descricao="Logins por semana das instituições canceladas, nas 12 semanas antes do cancelamento, contra a média semanal de uma instituição ativa."
      acao={
        <Select value={String(meses)} onValueChange={(v) => setMeses(Number(v))}>
          <SelectTrigger className="w-48" aria-label="Cancelamentos dos últimos">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6">Canceladas em 6 meses</SelectItem>
            <SelectItem value="12">Canceladas em 12 meses</SelectItem>
            <SelectItem value="24">Canceladas em 24 meses</SelectItem>
          </SelectContent>
        </Select>
      }
    >
      <GradeKpis variante="padrao" colunas={3}>
        <Kpi variante="padrao" rotulo="Cancelamentos no intervalo" valor={formatNumero(data?.canceladas.length)} isLoading={isLoading} />
        <Kpi
          variante="padrao"
          rotulo="Dias sem acesso ao cancelar (mediana)"
          valor={data?.medianaDiasSemAcessoNoCancelamento != null ? formatNumero(data.medianaDiasSemAcessoNoCancelamento) : "—"}
          detalhe="referência para calibrar o limite de inatividade"
          cor={CORES.vermelho}
          isLoading={isLoading}
        />
        <Kpi
          variante="padrao"
          rotulo="Logins/semana de uma ativa (média)"
          valor={data?.mediaSemanalAtivas != null ? data.mediaSemanalAtivas.toLocaleString("pt-BR", { maximumFractionDigits: 1 }) : "—"}
          cor={CORES.verde}
          isLoading={isLoading}
        />
      </GradeKpis>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5 shadow-card">
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <ChartContainer config={CONFIG_CHURN} className="aspect-auto h-64 w-full">
              <LineChart data={pontos} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="rotulo" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis tickLine={false} axisLine={false} width={40} />
                <ChartTooltip content={<ChartTooltipContent />} />
                {data?.mediaSemanalAtivas != null && (
                  <ReferenceLine
                    y={data.mediaSemanalAtivas}
                    stroke={CORES.verde}
                    strokeDasharray="4 4"
                    label={{ value: "média das ativas", position: "insideTopRight", fontSize: 12, fill: CORES.verde }}
                  />
                )}
                <Line dataKey="mediaLogins" type="monotone" stroke="var(--color-mediaLogins)" strokeWidth={2} isAnimationActive={false} />
              </LineChart>
            </ChartContainer>
          )}
        </div>

        <div className="max-h-80 overflow-auto rounded-xl border border-border bg-card shadow-card">
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : !data?.canceladas.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Nenhum cancelamento no intervalo.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Instituição</TableHead>
                  <TableHead>Cancelada em</TableHead>
                  <TableHead>Último login</TableHead>
                  <TableHead className="text-right">Dias sem acesso</TableHead>
                  <TableHead className="text-right">Logins (12 sem.)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.canceladas.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">
                      <Link to={`/Acessos/Instituicao/${c.id}`} className="hover:underline">
                        {c.nomeFantasia ?? `#${c.id}`}
                      </Link>
                    </TableCell>
                    <TableCell>{formatDateBr(c.dataTermino)}</TableCell>
                    <TableCell>{formatAcesso(c.ultimoAcesso)}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.diasSemAcessoNoCancelamento ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumero(c.logins12Semanas)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </Secao>
  );
}
